import type { MouseEvent } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { MILLISECONDS_TO_SECONDS } from '@/constants/time';
import { EventFilterView } from '@/features/EventFilterView';
import { useRelatedEvents } from '@/features/EventFeed/hooks';
import { EngineService } from '@/services/engine';
import {
    useDeletedItems,
    useOpenInNewWindow,
    useVideoSourceSelected,
} from '@/hooks';
import { useQueryFiltersStore, useSourcesStore } from '@/stores';
import { splitSourceSelection } from '@/stores/sources.store';
import { hasRouterHistory } from '@/stores/navigation-history.store';
import { getDateRangeFromFilter } from '@/utils/query-filters';
import { EVENTS_CONFIG } from '../constants';
import type { EventsPageScenario } from '../types';
import { useEventsPageFilters } from '../useEventsPageFilters';

export function useRelatedEventsScenario(): EventsPageScenario {
    const { t } = useTranslation([
        I18nNamespace.COMMON,
        I18nNamespace.EVENTS,
        I18nNamespace.SOURCES,
    ]);
    const { eventId, hasSourceFilter, setSearchParams } =
        useEventsPageFilters();
    const { openEvent } = useOpenInNewWindow();
    const selected = useSourcesStore((state) => state.selected);
    const selectedFolders = useSourcesStore((state) => state.selectedFolders);
    const dateTime = useQueryFiltersStore((state) => state.filters.dateTime);
    const selectedObjectClassIds = useQueryFiltersStore(
        (state) => state.selectedObjectClassIds,
    );
    const videoSourceSelected = useVideoSourceSelected(selected);

    const dateRange = useMemo(
        () => (videoSourceSelected ? null : getDateRangeFromFilter(dateTime)),
        [dateTime, videoSourceSelected],
    );

    const relatedFilters = useMemo(() => {
        const result: {
            sourceId?: string[];
            folderId?: string[];
            startTime?: number;
            endTime?: number;
            classId?: number[];
        } = {};

        if (selected.size > 0 || selectedFolders.size > 0) {
            const { folderIds, looseSourceIds } = splitSourceSelection(
                selected,
                selectedFolders,
            );
            if (folderIds.length > 0) {
                result.folderId = folderIds;
            }
            if (looseSourceIds.length > 0) {
                result.sourceId = looseSourceIds;
            }
        }

        if (dateRange) {
            result.startTime = Math.floor(
                dateRange.startDate.getTime() / MILLISECONDS_TO_SECONDS,
            );
            result.endTime = Math.floor(
                dateRange.endDate.getTime() / MILLISECONDS_TO_SECONDS,
            );
        }

        const classIds = Array.from(selectedObjectClassIds);
        if (classIds.length > 0) {
            result.classId = classIds;
        }

        return result;
    }, [selected, selectedFolders, dateRange, selectedObjectClassIds]);

    const selectionPending =
        hasSourceFilter && selected.size === 0 && selectedFolders.size === 0;

    const relatedResult = useRelatedEvents({
        eventId: selectionPending ? undefined : eventId,
        pageSize: 12,
        sourceId: relatedFilters.sourceId,
        folderId: relatedFilters.folderId,
        startTime: relatedFilters.startTime,
        endTime: relatedFilters.endTime,
        classId: relatedFilters.classId,
    });

    const items = useMemo(
        () =>
            relatedResult.data.map((scored) => ({
                event: scored.event,
                confidence: scored.confidence,
                sourceName: scored.event.sourceName ?? undefined,
            })),
        [relatedResult.data],
    );

    const onEventClick = useCallback(
        (clickedEventId: string, event?: MouseEvent) => {
            openEvent(clickedEventId, event, {
                state: {
                    from: 'related-events',
                    feedContext: {
                        type: 'relatedEvent',
                        eventId: eventId ?? clickedEventId,
                    },
                },
            });
        },
        [openEvent, eventId],
    );

    const { filteredItems, deletedCount } = useDeletedItems({
        items,
        getItemId: (item) => item.event.id,
        deleteItemsFn: (ids) => EngineService.deleteEvents(ids),
        successMessage: t('actions.deleteSuccess'),
        errorMessage: t('actions.deleteError'),
    });

    const sourceCount = useMemo(() => {
        if (relatedResult.hasMore) return undefined;
        return new Set(filteredItems.map((item) => item.event.sourceId)).size;
    }, [filteredItems, relatedResult.hasMore]);

    const handleClearRelatedEvent = useCallback(() => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete('eventId');
                return next;
            },
            { replace: true },
        );
    }, [setSearchParams]);

    return {
        pageKey: `related-events-${eventId}`,
        items: filteredItems,
        eventCount: relatedResult.data.length
            ? relatedResult.data.length - deletedCount
            : filteredItems.length,
        error: relatedResult.error,
        isLoading: relatedResult.isLoading,
        pagination: {
            mode: 'loadMore',
            isLoadingMore: relatedResult.isLoadingMore,
            hasMore: relatedResult.hasMore,
            onLoadMore: relatedResult.loadMore,
            loadingMessage: t('events:page.loading'),
            allLoadedMessage: t('events:page.allRelatedLoaded'),
        },
        isEmpty: filteredItems.length === 0,
        sourceCount,
        emptyMessage: t('events:page.noRelatedEvents'),
        showBackButton: !!eventId || hasSourceFilter || hasRouterHistory(),
        backButtonLabel: t('page.backToEvent'),
        filterView:
            hasSourceFilter || !!eventId ? (
                <EventFilterView
                    config={EVENTS_CONFIG}
                    relatedEventId={eventId}
                    onClearRelatedEvent={
                        eventId ? handleClearRelatedEvent : undefined
                    }
                />
            ) : undefined,
        onEventClick,
    };
}
